import { ENV } from "../Utils/constants";

/**
 * Cliente API del panel admin v2 de usuarios (M05).
 *
 * Coexiste con `User` (api/user.js) sin tocarlo. Cada metodo apunta a los
 * endpoints `/admin/users-v2` protegidos por `requireAdmin` en backend.
 *
 * Token: usa el accessToken del admin logueado (Bearer en Authorization).
 */
export class AdminUsersV2 {
  baseApi = ENV.BASE_API;

  /**
   * GET /admin/users-v2
   *
   * @param {string} accessToken
   * @param {Object} filters
   * @param {string} [filters.q]
   * @param {string[]} [filters.roles]
   * @param {string} [filters.vertical] - "educa"|"revi"|"simulator"|"otros".
   * @param {boolean} [filters.active]
   * @param {string} [filters.institutionId]
   * @param {string} [filters.partnerId]
   * @param {string} [filters.dateFrom] - ISO date.
   * @param {string} [filters.dateTo] - ISO date.
   * @param {boolean} [filters.orphans]
   * @param {number} [filters.page=1]
   * @param {number} [filters.limit=25]
   * @param {string} [filters.sort]
   */
  async list(accessToken, filters = {}) {
    const params = new URLSearchParams();
    if (filters.q) params.append("q", filters.q);
    if (Array.isArray(filters.roles) && filters.roles.length > 0) {
      params.append("roles", filters.roles.join(","));
    }
    if (filters.vertical) params.append("vertical", filters.vertical);
    if (filters.active !== undefined && filters.active !== null) {
      params.append("active", String(filters.active));
    }
    if (filters.institutionId)
      params.append("institutionId", filters.institutionId);
    if (filters.partnerId) params.append("partnerId", filters.partnerId);
    if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
    if (filters.dateTo) params.append("dateTo", filters.dateTo);
    if (filters.orphans) params.append("orphans", "true");
    if (filters.page) params.append("page", String(filters.page));
    if (filters.limit) params.append("limit", String(filters.limit));
    if (filters.sort) params.append("sort", filters.sort);

    const qs = params.toString();
    const url = `${this.baseApi}/admin/users-v2${qs ? `?${qs}` : ""}`;
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const result = await response.json();
    if (response.status !== 200) throw result;
    return result.data;
  }

  /**
   * GET /admin/users-v2/:id
   */
  async detail(accessToken, userId) {
    const url = `${this.baseApi}/admin/users-v2/${userId}`;
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const result = await response.json();
    if (response.status !== 200) throw result;
    return result.data;
  }

  /**
   * POST /admin/users-v2/:id/delete-preview
   * Devuelve el scope que se borraria sin ejecutar nada.
   */
  async deletePreview(accessToken, userId) {
    const url = `${this.baseApi}/admin/users-v2/${userId}/delete-preview`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({}),
    });
    const result = await response.json();
    if (response.status !== 200) throw result;
    return result.data;
  }

  /**
   * PATCH /admin/users-v2/:id
   * Edita un user con whitelist estricta (solo firstname/lastname/country/
   * phone/address/active/avatar). El backend rechaza cualquier otro campo.
   *
   * @param {string} accessToken
   * @param {string} userId
   * @param {Object} payload - solo campos seguros.
   * @param {File} [avatarFile] - opcional, multer con campo "avatar".
   */
  async update(accessToken, userId, payload, avatarFile) {
    const url = `${this.baseApi}/admin/users-v2/${userId}`;
    const formData = new FormData();
    Object.entries(payload || {}).forEach(([key, value]) => {
      if (value === undefined) return;
      if (value === null) {
        formData.append(key, "");
        return;
      }
      formData.append(key, typeof value === "boolean" ? String(value) : value);
    });
    if (avatarFile) formData.append("avatar", avatarFile);

    const response = await fetch(url, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${accessToken}` },
      body: formData,
    });
    const result = await response.json();
    if (response.status !== 200) throw result;
    return result.data;
  }

  /**
   * DELETE /admin/users-v2/:id
   * Ejecuta el cascade en alguno de los 3 modos.
   *
   * @param {string} mode - "deactivate" | "user-only" | "full".
   * @param {string} [confirmEmail] - obligatorio para modos irreversibles.
   */
  async deleteUser(accessToken, userId, mode, confirmEmail) {
    const url = `${this.baseApi}/admin/users-v2/${userId}`;
    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ cascade: mode, confirmEmail }),
    });
    const result = await response.json();
    if (response.status !== 200) throw result;
    return result.data;
  }
}
